// ONE POS brendi (Q-032). Belgi kod ichida chiziladi — internetsiz (kassa oflayn) ham chiqadi.
// Qoidalar: docs/BREND.md. Ranglarni o'zgartirmang, belgini burmang, cho'zmang.
import { useId } from 'react';
import { MARK_PATH, MARK_SMALL_PATH, STAR_PATH, WORD_ONE, WORD_POS } from './brandPaths';

export const BRAND = {
  emerald: '#014D48',
  saffron: '#DD9436',
  ink: '#0B2A33',
  mist: '#E6F0ED',
  name: 'ONE POS',
} as const;

/**
 * color — oq/och fonda; white — zumrad yoki to'q fonda; mono — bitta rang (currentColor);
 * auto — joriy mavzu foniga qarab (--op-brand-*); side — yon panel / qulf ekrani foniga qarab (--op-side-brand-*).
 */
export type BrandTone = 'color' | 'white' | 'mono' | 'auto' | 'side';

/** 32 px dan kichikda soddalashgan belgi ishlatiladi (harflar tutashib ketmasligi uchun). */
export const SMALL_MARK_BELOW = 32;

function inks(tone: BrandTone) {
  if (tone === 'white')
    return { mark: '#FFFFFF', star: BRAND.saffron, word: '#FFFFFF', product: '#FFFFFF' };
  if (tone === 'mono')
    return {
      mark: 'currentColor',
      star: 'currentColor',
      word: 'currentColor',
      product: 'currentColor',
    };
  if (tone === 'auto')
    return {
      mark: `var(--op-brand-mark, ${BRAND.emerald})`,
      star: BRAND.saffron,
      word: `var(--op-brand-word, ${BRAND.emerald})`,
      product: `var(--op-brand-product, ${BRAND.ink})`,
    };
  if (tone === 'side')
    return {
      mark: 'var(--op-side-brand-mark, #FFFFFF)',
      star: BRAND.saffron,
      word: 'var(--op-side-brand-word, #FFFFFF)',
      product: 'var(--op-side-brand-product, #FFFFFF)',
    };
  return { mark: BRAND.emerald, star: BRAND.saffron, word: BRAND.emerald, product: BRAND.ink };
}

function MarkShapes({ small, c, id }: { small: boolean; c: ReturnType<typeof inks>; id: string }) {
  if (!small)
    return (
      <>
        <path d={MARK_PATH} style={{ fill: c.mark }} fillRule="evenodd" />
        <path d={STAR_PATH} style={{ fill: c.star }} />
      </>
    );
  // Yulduz atrofida shaffof halqa: har qanday fonda (mono'da ham) yulduz ajralib turadi
  return (
    <>
      <mask id={`m${id}`}>
        <rect width="240" height="240" fill="#fff" />
        <path d={STAR_PATH} fill="#000" stroke="#000" strokeWidth="20" strokeLinejoin="round" />
      </mask>
      <path d={MARK_SMALL_PATH} style={{ fill: c.mark }} fillRule="evenodd" mask={`url(#m${id})`} />
      <path d={STAR_PATH} style={{ fill: c.star }} />
    </>
  );
}

interface MarkProps {
  size?: number;
  tone?: BrandTone;
  /** Majburan soddalashgan (true) yoki to'liq (false) belgi. Berilmasa — o'lchamga qarab. */
  simple?: boolean;
  /** Ekran o'quvchi uchun nom. Berilmasa — bezak sifatida yashiriladi. */
  title?: string;
  className?: string;
}

/** Faqat belgining o'zi (yozuvsiz). */
export function BrandMark({ size = 40, tone = 'color', simple, title, className }: MarkProps) {
  const id = useId().replace(/:/g, '');
  const c = inks(tone);
  const small = simple ?? size < SMALL_MARK_BELOW;
  const a11y = title ? { role: 'img', 'aria-label': title } : { 'aria-hidden': true as const };
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 240 240"
      focusable="false"
      data-brand-mark={small ? 'small' : 'full'}
      {...a11y}
    >
      <MarkShapes small={small} c={c} id={id} />
    </svg>
  );
}

const CAP = 96; // so'z bosh harfi balandligi (belgi 240 birlik)
const K = CAP / 100;
const GAP_MARK = 44;
const GAP_WORD = 56;

interface LockupProps {
  /** Logotip balandligi, px. */
  height?: number;
  tone?: BrandTone;
  /** Xizmat nomi: hozircha faqat POS. Berilmasa — faqat “ONE”. */
  product?: 'POS' | null;
  /** row — belgi chapda; stack — belgi tepada. */
  layout?: 'row' | 'stack';
  title?: string;
  className?: string;
}

/** Belgi + “ONE POS” yozuvi bitta SVG'da: o'lchamlar nisbati har doim bir xil. */
export function BrandLockup({
  height = 40,
  tone = 'color',
  product = 'POS',
  layout = 'row',
  title,
  className,
}: LockupProps) {
  const id = useId().replace(/:/g, '');
  const c = inks(tone);
  const oneW = (WORD_ONE.x1 - WORD_ONE.x0) * K;
  const posW = product ? (WORD_POS.x1 - WORD_POS.x0) * K : 0;
  const textW = oneW + (product ? GAP_WORD + posW : 0);
  const stack = layout === 'stack';
  const markSize = 240;
  const vbW = stack ? Math.max(markSize, textW) : markSize + GAP_MARK + textW;
  const vbH = stack ? markSize + 56 + CAP : markSize;
  const markX = stack ? (vbW - markSize) / 2 : 0;
  const textX = stack ? (vbW - textW) / 2 : markSize + GAP_MARK;
  const textY = stack ? markSize + 56 : (markSize - CAP) / 2;
  const width = Math.round((height * vbW) / vbH);
  const markPx = (height * markSize) / vbH;
  const a11y = { role: 'img', 'aria-label': title ?? (product ? `ONE ${product}` : 'ONE') };
  return (
    <svg
      className={className}
      width={width}
      height={height}
      viewBox={`0 0 ${vbW.toFixed(1)} ${vbH}`}
      focusable="false"
      {...a11y}
    >
      <g transform={`translate(${markX.toFixed(1)} 0)`}>
        <MarkShapes small={markPx < SMALL_MARK_BELOW} c={c} id={id} />
      </g>
      <path
        d={WORD_ONE.d}
        style={{ fill: c.word }}
        transform={`translate(${(textX - WORD_ONE.x0 * K).toFixed(1)} ${textY}) scale(${K})`}
      />
      {product && (
        <path
          d={WORD_POS.d}
          style={{ fill: c.product }}
          transform={`translate(${(textX + oneW + GAP_WORD - WORD_POS.x0 * K).toFixed(1)} ${textY}) scale(${K})`}
        />
      )}
    </svg>
  );
}
